// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { fontConfig } from "@pace/XDL/PreStyle/typography";
import { color, styledScrollbar, xScreen } from "@pace/XDL/Utilities";
import { css } from "styled-components";

export interface CategoryItemSharedStyles {
    container?: GridContainerProps;
    categoryItem?: GridItemProps;
    categoryImageItem?: InjectableCss;
    innerContainer?: InjectableCss;
    categoryContainer?: InjectableCss;
    categoryListHeader?: TypographyPresentationProps;
    categoryListHeaderGridItem?: GridItemProps;
    categoryImageLink?: LinkPresentationProps;
    categoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    subCategoryNameText?: TypographyPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
    subCategoryWrapper?: InjectableCss;
    accordionWrapper?: InjectableCss;
    categoryFiltersHeaderText?: TypographyPresentationProps;
    categoryDropdownGridItem?: GridItemProps;
}

const categoryItemSharedStyles: CategoryItemSharedStyles = {
    container: {
        gap: 22,
    },
    categoryItem: {
        width: [12, 6, 6, 4, 4],
        css: css`
            justify-content: center;
        `,
    },
    innerContainer: {
        css: css`
            display: flex;
            align-items: center;
            border-radius: 4px;
            border: 1px solid ${color("common.border")};
            height: 115px;
            padding: 13px;
            column-gap: 12px;
            flex: 1;
            &:hover{
                border-color: ${color("primary")}
            }
        `,
    },

    categoryImageItem: {
        css: css`
            width: 91px;
            height: auto;
        `,
    },

    categoryImage: {
        css: css`
            img {
                max-width: 100%;
                max-height: 100%;
                border-radius: 4px;
                height: auto;
            }
        `,
    },
    categoryListHeader: {
        variant: "h1",
        css: css`
            margin-bottom: 0;
        `,
    },

    categoryListHeaderGridItem: {
        width: 12,
    },

    subCategoryNameText: {
        weight: fontConfig.weight.bold,
        color: "text.main",
        css: css`
            width: 100%;
            text-align: left;
            word-wrap: break-word;
            line-height: 22px;
        `,
    },

    subCategoryNameLinkItem: {
        width: 12,
        css: css`
            padding-top: 0px;
            padding-bottom: 0px;
            &:nth-child(2) {
                .accordion-wrapper {
                    border-top: 1px solid ${color("common.border")};
                    border-radius: 4px 4px 0 0;
                }
            }
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    subCategoryNameLink: {
        typographyProps: {
            css: css`
                font-size: 12px;
            `,
        },
    },

    accordionSection: {
        titleTypographyProps: {
            size: 12,
            lineHeight: "13px",
            fontFamily: fontConfig.fontFamily,
            weight: fontConfig.weight.regular,
        },
        toggleIconProps: {
            size: 10,
        },
        collapseIconProps: {
            size: 10,
        },
        panelProps: {
            css: css`
                overflow-y: hidden;
                overflow-x: hidden;
                max-height: 250px;
                width: 100%;
                padding: 8px 0;
            `,
        },
        headerProps: {
            css: css`
                ${TypographyStyle} {
                    font-weight: ${fontConfig.weight.bold};
                    font-size: 12px;
                    line-height: 17px;
                }
                &&&& button {
                    border: 0;
                    margin-bottom: 0;
                    padding: 0;
                    &:focus {
                        border: 0;
                        background: ${color("common.background")};
                        outline-width: 0;
                    }
                }
            `,
        },
    },
    subCategoryWrapper: {
        css: css`
            position: relative;
            overflow-x: hidden;
            max-height: 165px;
            overflow-y: hidden;
            ${styledScrollbar()};
            display: flex;
            flex-direction: column;
            row-gap: 12px;
        `,
    },
    accordionWrapper: {
        css: css`
            width: 319px;
            border: 1px solid ${color("common.border")};
            border-top: 0;
            padding: 9px;
        `,
    },
    categoryFiltersHeaderText: {
        variant: "h5",
        size: 18,
        css: css`
            line-height: 22px;
            margin-bottom: 8px;
        `,
    },
    categoryDropdownGridItem: {
        width: 12,
        css: css`
            display: none;
            ${xScreen("lte", "md")} {
                display: flex;
            }
        `,
    },
};

export default categoryItemSharedStyles;
